"use client";

import { createUserAssociation } from "deso-protocol";
import Link from "next/link";
import { useContext, useState } from "react";
import {
  LuActivity,
  LuBan,
  LuCircleCheck as LuCheckCircle,
  LuCopy,
  LuEllipsis as LuMoreHorizontal,
  LuExternalLink,
  LuFlag,
  LuLandmark,
  LuMail,
  LuPencil,
  LuSend,
  LuShield,
  LuWallet,
} from "react-icons/lu";

import { authorizeVerifier, deauthorizeVerifier } from "@/backend/focus";
import { setDialog } from "@/components/app/dialogs/dialog-provider";
import { Button } from "@/components/ui/button";
import {
  DropdownDrawer,
  DropdownDrawerContent,
  DropdownDrawerItem,
  DropdownDrawerSeparator,
  DropdownDrawerSub,
  DropdownDrawerSubContent,
  DropdownDrawerSubTrigger,
  DropdownDrawerTrigger,
} from "@/components/ui/dropdown-drawer";
import { useToast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import { useBlockUser } from "@/hooks/use-block-user";
import { useGetUserBlockedState } from "@/hooks-api/use-get-user-blocked-state";
import { APP_ROUTES, USER_REPORT_TYPE } from "@/utils/constants";
import { DEFAULT_REPORT_TYPE } from "@/utils/constants";
import { DIALOG } from "@/utils/dialog-routes";
import { copyTextToClipboard } from "@/utils/helpers";
import { buildDMThreadIdentifier } from "@/utils/messages";
import type { PartialAccount } from "@/utils/types";
import { getUserName } from "@/utils/usernames";

interface PostActionsProps {
  account: PartialAccount;
  onRefetch?: (publicKey: string) => Promise<void>;
  menuAlign?: "start" | "center" | "end";
  onBlock?: (blocked: boolean) => void;
}
export const ProfileActionsDropdown = ({
  account,
  onRefetch,
  menuAlign = "end",
  onBlock,
}: PostActionsProps) => {
  const {
    toggleBlockUser,
    isElevated,
    isAdmin,
    currentUserPublicKey,
    isVerifier,
    isSuperAdmin,
  } = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const publicKey = account.publicKey ?? "";
  const username = account.username ?? "";
  const isBlacklisted = !!account?.isBlacklisted;
  const isGreylisted = (account?.isGreylisted?.totalCount ?? 0) > 0;
  const isNSFW = (account?.isNSFW?.totalCount ?? 0) > 0;
  const isAccountVerifier = (account?.isVerifier?.totalCount ?? 0) > 0;
  const isAccountVerified = !!account?.isVerified;
  const { toast } = useToast();

  const { blocked, refetch, assosiationId } = useGetUserBlockedState({
    transactorPublicKey: currentUserPublicKey,
    userPublicKey: publicKey,
  });
  const { blockUser, unblockUser, loading } = useBlockUser();

  const isMyAccount = currentUserPublicKey === publicKey;

  const formattedUsername = getUserName({
    account,
  });
  const messageHref =
    publicKey && currentUserPublicKey && !isMyAccount
      ? `/messages?threadId=${buildDMThreadIdentifier(publicKey, currentUserPublicKey)}`
      : "/messages";

  const copyProfileValue = async (text: string, description: string) => {
    try {
      await copyTextToClipboard(text);
      toast({
        variant: "success",
        title: "Success",
        duration: 2500,
        description,
      });
    } catch {
      toast({
        title: "Error",
        description: "An error happened when copying the text",
        variant: "destructive",
      });
    }
  };

  const toggleBlock = async () => {
    if (blocked) {
      if (assosiationId) {
        await unblockUser(assosiationId);
      }
    } else {
      await blockUser(publicKey);
    }

    toggleBlockUser(publicKey);
    await refetch();
    onBlock?.(!blocked);
  };

  const openProfileReport = (
    defaultType = DEFAULT_REPORT_TYPE,
    showTypes = true,
  ) => {
    setDialog(DIALOG.ReportUser, {
      open: true,
      replace: true,
      params: {
        allowReverse: String(isAdmin),
        defaultType,
        isBlacklisted: String(isBlacklisted),
        isGreylisted: String(isGreylisted),
        isNSFW: String(isNSFW),
        publicKey,
        showTypes: String(showTypes),
      },
    });
  };

  return (
    <div>
      <DropdownDrawer
        historyName="profile-actions"
        open={open}
        onOpenChange={setOpen}
        title="Profile actions"
      >
        <DropdownDrawerTrigger
          render={
            <Button
              variant="outline"
              size="icon"
              className="rounded-full bg-background"
            >
              <LuMoreHorizontal />
            </Button>
          }
        />

        <DropdownDrawerContent align={menuAlign}>
          {isMyAccount && (
            <DropdownDrawerItem
              onClick={() =>
                setDialog(DIALOG.UpdateProfile, {
                  open: true,
                  replace: true,
                  props: {
                    onUpdated: async (updatedPublicKey: string) => {
                      await onRefetch?.(updatedPublicKey);
                    },
                  },
                })
              }
            >
              <LuPencil className="mr-2 size-4 text-muted" />
              Edit Profile
            </DropdownDrawerItem>
          )}

          <DropdownDrawerItem
            onClick={() =>
              copyProfileValue(
                `${process.env.NEXT_PUBLIC_FOCUS_URI}/${username}`,
                `${formattedUsername}'s profile URL has been successfully copied to your clipboard.`,
              )
            }
          >
            <LuCopy className="mr-2 size-4 text-muted" />
            Copy Link
          </DropdownDrawerItem>

          <DropdownDrawerItem
            onClick={() =>
              copyProfileValue(
                publicKey,
                `${formattedUsername}'s public key has been successfully copied to your clipboard.`,
              )
            }
          >
            <LuCopy className="mr-2 size-4 text-muted" />
            Copy Public Key
          </DropdownDrawerItem>

          {!isMyAccount && (
            <DropdownDrawerItem onClick={toggleBlock} disabled={loading}>
              <LuBan className="mr-2 size-4 text-muted" />
              {blocked ? "Unblock" : "Block"} User
            </DropdownDrawerItem>
          )}

          {!isMyAccount && (
            <DropdownDrawerItem
              className="sm:hidden"
              render={
                <Link href={messageHref} prefetch={false}>
                  <LuMail className="mr-2 size-4 text-muted" />
                  Send Message
                </Link>
              }
            />
          )}

          <DropdownDrawerItem
            render={
              <Link href={`${APP_ROUTES.wallet}/${username}`}>
                <LuWallet className="mr-2 size-4 text-muted" />
                View Wallet
              </Link>
            }
          />

          <DropdownDrawerItem
            render={
              <Link href={`${APP_ROUTES.earnings}/${username}`}>
                <LuLandmark className="mr-2 size-4 text-muted" />
                View Earnings
              </Link>
            }
          />

          {!isMyAccount && (
            <DropdownDrawerItem
              onClick={() =>
                setDialog(DIALOG.WalletSend, {
                  open: true,
                  replace: true,
                  params: {
                    receiver: publicKey,
                  },
                })
              }
            >
              <LuSend className="mr-2 size-4 text-muted" />
              Send Money
            </DropdownDrawerItem>
          )}

          <DropdownDrawerItem
            render={
              <Link
                href={`${process.env.NEXT_PUBLIC_WALLET_URI}/?tab=activity&user=${username ?? publicKey}`}
                target="_blank"
              >
                <LuActivity className="mr-2 size-4 text-muted" />
                <span className="mr-auto">View Activity</span>
                <LuExternalLink className="ml-auto size-4 text-muted" />
              </Link>
            }
          />

          <DropdownDrawerItem
            onClick={() => openProfileReport(DEFAULT_REPORT_TYPE, isElevated)}
          >
            <LuFlag className="mr-2 size-4 text-muted" />
            <span>Report User</span>
          </DropdownDrawerItem>

          {(isVerifier || isElevated) && (
            <>
              <DropdownDrawerSeparator />
              {isElevated && (
                <DropdownDrawerSub>
                  <DropdownDrawerSubTrigger>
                    <LuShield className="mr-2 size-4 text-muted" />
                    Moderation
                  </DropdownDrawerSubTrigger>
                  <DropdownDrawerSubContent title="Moderation">
                    <DropdownDrawerItem
                      onClick={() =>
                        openProfileReport(USER_REPORT_TYPE.NSFW.value)
                      }
                    >
                      <span>
                        {USER_REPORT_TYPE.NSFW.getLabel({
                          allowReverse: isAdmin,
                          isNSFW,
                        })}
                      </span>
                    </DropdownDrawerItem>
                    <DropdownDrawerItem
                      onClick={() =>
                        openProfileReport(USER_REPORT_TYPE.GREYLIST.value)
                      }
                    >
                      <span>
                        {USER_REPORT_TYPE.GREYLIST.getLabel({
                          allowReverse: isAdmin,
                          isGreylisted,
                        })}
                      </span>
                    </DropdownDrawerItem>
                    <DropdownDrawerItem
                      onClick={() =>
                        openProfileReport(USER_REPORT_TYPE.BLACKLIST.value)
                      }
                    >
                      <span>
                        {USER_REPORT_TYPE.BLACKLIST.getLabel({
                          allowReverse: isAdmin,
                          isBlacklisted,
                        })}
                      </span>
                    </DropdownDrawerItem>
                  </DropdownDrawerSubContent>
                </DropdownDrawerSub>
              )}
              {(isVerifier || isSuperAdmin) && (
                <DropdownDrawerSub>
                  <DropdownDrawerSubTrigger>
                    <LuCheckCircle className="mr-2 size-4 text-muted" />
                    Verification
                  </DropdownDrawerSubTrigger>
                  <DropdownDrawerSubContent title="Verification">
                    {isSuperAdmin && !isAccountVerifier && (
                      <DropdownDrawerItem
                        onClick={async () => {
                          await authorizeVerifier({
                            publicKey,
                            adminPublicKey: currentUserPublicKey,
                          });
                          toast({
                            title: "User is now a Verifier",
                            description: `${formattedUsername} has been granted Verifier status.`,
                            variant: "success",
                          });
                        }}
                      >
                        <span>Make user a Verifier</span>
                      </DropdownDrawerItem>
                    )}
                    {isSuperAdmin && isAccountVerifier && (
                      <DropdownDrawerItem
                        onClick={async () => {
                          await deauthorizeVerifier({
                            publicKey,
                            adminPublicKey: currentUserPublicKey,
                          });
                          toast({
                            title: "User is no longer a Verifier",
                            description: `${formattedUsername} has been removed from Verifier status.`,
                            variant: "success",
                          });
                        }}
                      >
                        <span>Remove Verifier status</span>
                      </DropdownDrawerItem>
                    )}
                    {isVerifier && isAccountVerified && (
                      <DropdownDrawerItem
                        onClick={async () => {
                          await createUserAssociation({
                            AssociationType: "VERIFIED",
                            AssociationValue: "UNVERIFIED",
                            AppPublicKeyBase58Check:
                              process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
                            TransactorPublicKeyBase58Check:
                              currentUserPublicKey,
                            TargetUserPublicKeyBase58Check: publicKey,
                          });
                          toast({
                            title: "User is now Unverified",
                            description: `${formattedUsername} has been removed from Verified status.`,
                            variant: "success",
                          });
                        }}
                      >
                        <span>Remove Verified status</span>
                      </DropdownDrawerItem>
                    )}
                    {isVerifier && !isAccountVerified && (
                      <DropdownDrawerItem
                        onClick={async () => {
                          await createUserAssociation({
                            AssociationType: "VERIFIED",
                            AssociationValue: "VERIFIED",
                            AppPublicKeyBase58Check:
                              process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
                            TransactorPublicKeyBase58Check:
                              currentUserPublicKey,
                            TargetUserPublicKeyBase58Check: publicKey,
                          });
                          toast({
                            title: "User is now Verified",
                            description: `${formattedUsername} has been granted Verified status.`,
                            variant: "success",
                          });
                        }}
                      >
                        <span>Make user Verified</span>
                      </DropdownDrawerItem>
                    )}
                  </DropdownDrawerSubContent>
                </DropdownDrawerSub>
              )}
            </>
          )}
        </DropdownDrawerContent>
      </DropdownDrawer>
    </div>
  );
};
